import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { Languages, LogOut, Check, Loader2, ExternalLink, ShieldCheck, Sparkles } from "lucide-react";

const BG_IMAGE = "https://images.unsplash.com/photo-1764314138160-5f04f4a50dae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxwaHlzaWNhbCUyMHRoZXJhcGlzdCUyMGNsaW5pY3xlbnwwfHx8fDE3NzgxMDM2NzJ8MA&ixlib=rb-4.1.0&q=85";

const SOLO_FEATURES = [
  "AI translation in 60+ languages",
  "Whisper voice dictation + auto-translate",
  "OpenAI TTS in 5 voices",
  "OT/ST/PT phrase library",
  "Patient cards with encrypted notes",
  "Session log with full audit trail",
];

const COMPANY_FEATURES = [
  "Everything in Solo",
  "Per-seat billing — invite your team",
  "Centralized admin & billing",
  "Shared phrase library access",
  "Volume-friendly pricing",
];

export default function Billing() {
  const { user, logout, refreshUser } = useAuth();
  const nav = useNavigate();
  const [seats, setSeats] = useState(3);
  const [yearly, setYearly] = useState(false);
  const [powerPack, setPowerPack] = useState(false);
  const [busy, setBusy] = useState(null); // 'solo' | 'company' | 'portal' | 'pp-on' | 'pp-off' | null

  // If user already has an active sub, do NOT auto-redirect (they may want to manage Power Pack here).
  // Solo monthly = 19, yearly = 190 (2 months free)
  // Company monthly = 15/seat, yearly = 150/seat
  const soloPrice = yearly ? 190 : 19;
  const soloPeriod = yearly ? "year" : "month";
  const companyPrice = yearly ? 150 : 15;
  const companyPeriod = yearly ? "/seat/year" : "/seat/month";
  const ppPrice = yearly ? 90 : 9;

  const checkout = async (planKind) => {
    setBusy(planKind);
    try {
      const planId = `${planKind}_${yearly ? "yearly" : "monthly"}`;
      const { data } = await api.post("/billing/checkout", {
        plan: planId,
        seats: planKind === "company" ? Math.max(1, parseInt(seats) || 1) : 1,
        origin_url: window.location.origin,
        power_pack: powerPack,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const fd = new FormData();
      fd.append("origin_url", window.location.origin);
      const { data } = await api.post("/billing/portal", fd);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open billing portal");
      setBusy(null);
    }
  };

  const togglePowerPackForActiveSub = async (next) => {
    setBusy(next ? "pp-on" : "pp-off");
    try {
      await api.post(`/billing/power-pack/${next ? "enable" : "disable"}`);
      await refreshUser();
      toast.success(next ? "AI Power Pack added" : "AI Power Pack removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change add-on");
    } finally {
      setBusy(null);
    }
  };

  const isOrgMember = user?.org_id && user?.org_role !== "admin";
  const sub = user?.subscription_status;
  const hasActive = user?.has_active_subscription;

  return (
    <div
      className="min-h-screen"
      style={{ backgroundImage: `linear-gradient(rgba(255,255,255,0.96), rgba(255,255,255,0.96)), url(${BG_IMAGE})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <header className="border-b border-zinc-200 bg-white/80 backdrop-blur-sm">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-zinc-900 flex items-center justify-center">
              <Languages className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-heading text-lg tracking-tight leading-none">Clinico Translate</p>
              <p className="text-xs text-zinc-500 mt-0.5">Billing</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasActive && (
              <Button variant="outline" size="sm" onClick={() => nav("/app")} data-testid="billing-back-app">
                Back to app
              </Button>
            )}
            <p className="hidden sm:block text-sm text-zinc-600">{user?.email}</p>
            <Button variant="outline" size="sm" onClick={() => { logout(); nav("/login"); }} data-testid="billing-logout-button">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1280px] mx-auto px-4 md:px-8 py-12">
        {!hasActive && (
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-3">Choose your plan</p>
            <h1 className="font-heading text-4xl md:text-6xl tracking-tight leading-none text-zinc-900">
              Pick a plan to start your 7-day trial.
            </h1>
            <p className="text-zinc-600 mt-5 text-lg leading-relaxed">
              Both plans include a 7-day free trial. Card required upfront — you can cancel any time before day 7 and won't be charged.
            </p>
          </div>
        )}

        {hasActive && (
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-3">Your subscription</p>
            <h1 className="font-heading text-4xl md:text-5xl tracking-tight leading-none text-zinc-900">
              You're on {user?.plan?.includes("yearly") ? "the annual plan" : "the monthly plan"}.
            </h1>
            <p className="text-zinc-600 mt-5 text-lg leading-relaxed">
              Status: <span className="font-medium text-zinc-900">{sub}</span>
              {user?.trial_ends_at && ` · trial ends ${new Date(user.trial_ends_at).toLocaleDateString()}`}
              {user?.current_period_end && ` · renews ${new Date(user.current_period_end).toLocaleDateString()}`}
            </p>
            <div className="mt-6 flex gap-3 flex-wrap">
              <Button onClick={openPortal} disabled={busy === "portal"} data-testid="open-portal-button-active">
                {busy === "portal" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4 mr-2" /> Manage billing</>}
              </Button>
            </div>
          </div>
        )}

        {isOrgMember && (
          <div className="mt-8 border border-zinc-200 rounded-xl bg-white p-6 max-w-3xl" data-testid="org-member-notice">
            <p className="font-heading text-2xl tracking-tight text-zinc-900 mb-2">You're part of a clinic</p>
            <p className="text-zinc-600">
              Your organization's admin manages billing on your behalf. {hasActive ? "You have full access." : "If they have an active subscription, you'll have full access automatically."}
            </p>
            <p className="text-sm text-zinc-500 mt-3">Status: {sub || "no active subscription"}</p>
          </div>
        )}

        {/* Power Pack section for active subs */}
        {hasActive && !isOrgMember && (
          <div className="mt-10 border border-zinc-200 rounded-xl bg-white p-6 max-w-3xl" data-testid="power-pack-active-card">
            <div className="flex items-start gap-4 flex-wrap justify-between">
              <div className="flex items-start gap-3 max-w-xl">
                <div className="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-heading text-xl tracking-tight text-zinc-900">AI Power Pack</p>
                  <p className="text-sm text-zinc-600 mt-1">Upgrades translation to <span className="font-medium">Claude Opus 4.5</span> and TTS to <span className="font-medium">tts-1-hd</span>. {yearly ? "$90/yr" : "$9/mo"}, prorated.</p>
                </div>
              </div>
              <Switch
                checked={!!user?.power_pack}
                onCheckedChange={togglePowerPackForActiveSub}
                disabled={busy === "pp-on" || busy === "pp-off"}
                data-testid="power-pack-toggle-active"
              />
            </div>
          </div>
        )}

        {/* Plan picker for users without sub */}
        {!hasActive && !isOrgMember && (
          <>
            {/* Monthly / Yearly toggle */}
            <div className="mt-10 inline-flex items-center gap-3 bg-white border border-zinc-200 rounded-full p-1" data-testid="billing-cycle-toggle">
              <button
                onClick={() => setYearly(false)}
                className={`px-5 h-10 rounded-full text-sm font-medium transition-colors ${!yearly ? "bg-zinc-900 text-white" : "text-zinc-700"}`}
                data-testid="cycle-monthly"
              >
                Monthly
              </button>
              <button
                onClick={() => setYearly(true)}
                className={`px-5 h-10 rounded-full text-sm font-medium transition-colors ${yearly ? "bg-zinc-900 text-white" : "text-zinc-700"}`}
                data-testid="cycle-yearly"
              >
                Annual <span className={`ml-2 ${yearly ? "text-emerald-300" : "text-emerald-600"}`}>· save 2 mo</span>
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8 max-w-5xl">
              {/* Solo card */}
              <div className="border border-zinc-200 rounded-xl bg-white p-8 flex flex-col" data-testid="plan-card-solo">
                <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-3">Solo</p>
                <h2 className="font-heading text-3xl tracking-tight text-zinc-900">For individual clinicians</h2>
                <div className="mt-6 mb-6">
                  <span className="font-heading text-5xl tracking-tight">${soloPrice}</span>
                  <span className="text-zinc-500"> / {soloPeriod}</span>
                  {yearly && <p className="text-sm text-emerald-700 mt-1">Just ${(190/12).toFixed(2)}/mo, billed annually</p>}
                </div>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {SOLO_FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-700">
                      <Check className="w-4 h-4 text-zinc-900 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full h-12 text-base"
                  onClick={() => checkout("solo")}
                  disabled={busy === "solo"}
                  data-testid="checkout-solo-button"
                >
                  {busy === "solo" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start 7-day trial · Solo"}
                </Button>
              </div>

              {/* Company card */}
              <div className="border-2 border-zinc-900 rounded-xl bg-white p-8 flex flex-col relative" data-testid="plan-card-company">
                <span className="absolute -top-3 left-8 bg-zinc-900 text-white text-xs px-3 py-1 rounded-full uppercase tracking-[0.15em]">For clinics</span>
                <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-3">Company</p>
                <h2 className="font-heading text-3xl tracking-tight text-zinc-900">For clinics & multi-therapist practices</h2>
                <div className="mt-6 mb-2">
                  <span className="font-heading text-5xl tracking-tight">${companyPrice}</span>
                  <span className="text-zinc-500"> {companyPeriod}</span>
                </div>
                <p className="text-sm text-zinc-500 mb-6">Add or remove seats anytime from the billing portal.</p>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {COMPANY_FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-700">
                      <Check className="w-4 h-4 text-zinc-900 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mb-4">
                  <label className="text-sm text-zinc-700 flex items-center gap-3" htmlFor="seats-input">
                    <span>Seats</span>
                    <input
                      id="seats-input"
                      type="number"
                      min={1}
                      max={500}
                      value={seats}
                      onChange={(e) => setSeats(e.target.value)}
                      className="h-11 px-3 w-24 border border-zinc-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-zinc-900"
                      data-testid="seats-input"
                    />
                    <span className="text-zinc-500">= ${companyPrice * Math.max(1, parseInt(seats) || 1)}/{yearly ? "yr" : "mo"} total</span>
                  </label>
                </div>
                <Button
                  className="w-full h-12 text-base"
                  onClick={() => checkout("company")}
                  disabled={busy === "company"}
                  data-testid="checkout-company-button"
                >
                  {busy === "company" ? <Loader2 className="w-4 h-4 animate-spin" /> : `Start 7-day trial · ${seats || 1} seats`}
                </Button>
              </div>
            </div>

            {/* Power Pack add-on at signup */}
            <div className="mt-6 border border-zinc-200 rounded-xl bg-white p-6 max-w-5xl" data-testid="power-pack-signup-card">
              <div className="flex items-start gap-4 flex-wrap justify-between">
                <div className="flex items-start gap-3 max-w-xl">
                  <div className="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center shrink-0">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-heading text-xl tracking-tight text-zinc-900">Add AI Power Pack · ${ppPrice}/{yearly ? "yr" : "mo"}</p>
                    <p className="text-sm text-zinc-600 mt-1">
                      Premium <span className="font-medium">Claude Opus 4.5</span> translations and <span className="font-medium">HD voice</span> output. Cancel anytime from the billing portal.
                    </p>
                  </div>
                </div>
                <Switch checked={powerPack} onCheckedChange={setPowerPack} data-testid="power-pack-toggle-signup" />
              </div>
            </div>
          </>
        )}

        <div className="mt-12 max-w-3xl flex items-start gap-3 text-sm text-zinc-500">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <p>
            Payments are processed by Stripe. We never see or store your card details. Patient notes are encrypted at rest with AES-128 and every PHI access is recorded in your audit log.
          </p>
        </div>
      </main>
    </div>
  );
}
